import isFeatureToggleOn from "@javascripts/utils/feature_toggle"
import DocumentAdapter from "@javascripts/adapters/document_adapter";
import PubmaticAdapter from "@javascripts/adapters/pubmatic_adapter";
import PubmaticAdapterV2 from "./adapters/pubmatic_adapter_v2";
import URLUtils from "@javascripts/utils/url_utils";
import Process from "./process";
import localStorageAdapter from '@javascripts/adapters/localstorage_adapter';
import AdTag from '@javascripts/advertisements/ad_tag';
import { PlayerEnvironment } from "./player_environment";
import CookieUtils from "@javascripts/utils/cookie_utils";
import AhoyAdapter from "@javascripts/adapters/ahoy_adapter";

export class PlayerAdvertisementParams {
  constructor(playerData, muted){
    this._prerollAdTag = new AdTag();
    this._tvcReplacementAdTag = new AdTag();
    this._playerData = playerData;
    this._muted = muted;
    this._hermesVideoConfig = {};
    this._nonTimeConsumingCuePoints = [];
  }

  playerData() {
    return this._playerData;
  }

  adsEnabled() {
    return this.playerData().adsEnabled();
  }

  contentType() {
    return this.playerData().type();
  }

  contentId() {
    return this.playerData().videoId();
  }

  embed() {
    return this.playerData().embed() == "true";
  }

  sticky() {
    return this.playerData().sticky();
  }

  embedPosition(){
    return this.playerData().embedPosition();
  }

  adConfig() {
    return this.playerData().adConfig();
  }

  muted() {
    return this._muted;
  }

  prerollAdTag() {
    return this._prerollAdTag;
  }

  prerollTag(){
    return this.prerollAdTag().getAdTag();
  }

  prerollAdUnit(){
    return this.prerollAdTag().getAdUnit();
  }

  prerollAdSize(){
    return this.prerollAdTag().getAdSize();
  }

  adTagIsVmap() {
    return this.prerollAdTag().isVmap();
  }
  
  tvcReplacementAdTag() {
    return this._tvcReplacementAdTag;
  }

  tvcReplacementTag() {
    return this.tvcReplacementAdTag().getAdTag();
  }

  pubmaticUrl() {
    return this._pubmaticUrl;
  }

  present(){
    return (this.prerollTag && this.prerollTag() != "");
  }

  visitorId() {
    return CookieUtils.getCookie('he_visitor_id') || AhoyAdapter.global().visitorId || ""
  }

  hermesVideoConfig() {
    return this._hermesVideoConfig;
  }

  nonTimeConsumingCuePoints() {
    return this._nonTimeConsumingCuePoints;
  }

  squeezeFrameCuePoints() {
    return this.nonTimeConsumingCuePoints().filter((cuePoint) => cuePoint.type == "squeeze_frame");
  }

  superimposeCuePoints() {
    return this.nonTimeConsumingCuePoints().filter((cuePoint) => cuePoint.type == "superimpose");
  }

  tickerTapeCuePoints() {
    return this.nonTimeConsumingCuePoints().filter((cuePoint) => cuePoint.type == "ticker_tape");
  }

  setPrerollAdTag(url) {
    this._prerollAdTag = new AdTag(url);

    if (url != "") {
      this._prerollAdTag
        .addNetworkTypeToAdTag()
        .addVidioEmbedSite(this.embed())
        .replaceVidioMuted(this.muted())
        .setChildDirectedTreatment();
    } 
  }

  setTvcReplacementAdTag(url) {
    this._tvcReplacementAdTag = new AdTag(url);

    if (url != "") {
      this._tvcReplacementAdTag
        .addNetworkTypeToAdTag()
        .addVidioEmbedSite(this.embed())
        .replaceVidioMuted(this.muted())
        .setChildDirectedTreatment();
    }
  }

  setPubmaticUrl(url) {
    this._pubmaticUrl = url;
  }

  setIasConfig() {
    window.iasConfig = {
      anId: '931457',
      chanId: this.prerollAdUnit(),
      campId: this.prerollAdSize(),
    }
  }

  setHermesVideoConfig(config) {
    this._hermesVideoConfig = config;
  }

  setNonTimeConsumingCuePoints(cuePoints) {
    this._nonTimeConsumingCuePoints = cuePoints || [];
  }

  getVideoAdsParamsViaApi(callback) {
    if(this.adsEnabled()) {
      const endpoint = this.adsParamsApiEndpoint();

      const xhr = new XMLHttpRequest();
      xhr.timeout = 1000;
      xhr.open("GET", endpoint);
      xhr.withCredentials = true;

      xhr.onload = () => {
        if (xhr.status == 200 && xhr.response) {
          const xhr_props = {
            response: xhr.response,
            contentType: xhr.getResponseHeader("content-type"),
            responseHeaders: xhr.getAllResponseHeaders(),
          };
          this.adsParamsApiOnSuccess(xhr_props, callback);
        } else {
          callback();
        }
      };

      xhr.ontimeout = function() {
        callback();
      }

      xhr.onerror = function() {
        callback();
      }

      xhr.send();
    }
    else {
      this.appendOpenWrapTargeting(callback);
    }
  }

  hermesEndpoint() {
    if (Process.env().RAILS_ENV == "test") {
      return `/api/tags/${this.adConfig()}`;
    }

    let url = "https://hermes.staging.vidio.com/api/tags";
    if (Process.env().PROJECT_ENVIRONMENT === "prod") {
      url = "https://hermes.vidio.com/api/tags";
    }

    url = this.addHermesQueryParams(url);

    return url;
  }

  // Warning: this method is also used by Vidio repository
  addHermesQueryParams(url) {
    let queryParams = {
      cp: this.playerData().isPremier(),
      ct: this.playerData().type(),
      durl: this.playerData().watchpageUrl(),
      cid: this.playerData().videoId(),
      cuu: this.playerData().videoUsername(),
      cuid: this.playerData().videoUserId(),
      cmg: this.playerData().contentMainGenre(),
      cg: this.playerData().contentGenres(),
      cc: this.playerData().contentCountry(),
      cgp: this.playerData().contentGroup(),
      ce: this.playerData().contentEvents(),
      cro: this.playerData().contentRatingOption(),
      e: this.embed(),
      s: this.sticky(),
      cct: this.playerData().contentContentType(),
      ep: this.playerData().embedPosition(),
      cur: this.playerData().videoUserRole(),
      vt: this.playerData().videoTags(),
      vfid: this.playerData().filmId(),
      vft: this.playerData().filmTitle(),
      vfg: this.playerData().filmGenres(),
      vc: this.playerData().videoChannel(),
      vcid: this.playerData().videoChannelId(),
      cpr: this.playerData().preview(),
      lst: this.playerData().livestreamingTags(),
      vvid: this.visitorId(),
    };
    
    let device = "desktop";
    if (PlayerEnvironment.isMobileSite() || PlayerEnvironment.isLiteSite()) {
      device = "mobile";
    }
    queryParams["d"] = device;

    const ref = DocumentAdapter.referrer();
    const referrer = ref.split('/')[2];
    if (referrer) {
      queryParams["rdm"] = referrer;
      queryParams["rdv"] = device;
    }

    const userId = localStorageAdapter("video_ads").getItem("vidio_user_id") || CookieUtils.getCookie('plenty_id');
    if (userId) {
      queryParams["wid"] = userId;
    }

    for (const [key, value] of Object.entries(queryParams)) {
      url = URLUtils.addQueryParam(url, key, encodeURIComponent(value));
    }

    return url;
  }

  adsParamsApiEndpoint() {
    return this.hermesEndpoint();
  }

  adsParamsApiOnSuccess(xhr_props, callback) {
    this.hermesAdsParamsApiOnSuccess(xhr_props, callback);
  }

  hermesAdsParamsApiOnSuccess(xhr_props, callback) {
    try {
      const json = JSON.parse(xhr_props.response);
      
      this.setPrerollAdTag(json.video.instream);
      this.setTvcReplacementAdTag(json.video.tvc_replacement);
      this.setPubmaticUrl(json.video.header_bidding.pubmatic);

      this.setIasConfig();
      this.setHermesVideoConfig(json.video.config);

      this.setNonTimeConsumingCuePoints(json.display.non_time_consuming.cue_points);
      this.appendOpenWrapTargeting(callback);
    } catch (e) {
      callback();
    }
  }
  
  appendOpenWrapTargeting(callback) {
    if(this.shouldAppendOpenWrapTargeting()) {
      if(this.shouldUsePubmaticAdapterV2()) {
        PubmaticAdapterV2.appendOpenWrapTargeting(
          this.prerollTag(),
          this.prerollAdUnit(),
          this.prerollAdSize(),
          this.openWrapCallback(callback)
        );
      } else {
        PubmaticAdapter.appendOpenWrapTargeting(
          this.prerollTag(),
          this.pubmaticUrl(),
          this.openWrapCallback(callback)
        );
      }
    }
    else {
      callback();
    }
  }

  shouldAppendOpenWrapTargeting() {
    return this.prerollTag() &&
           this.prerollTag() != "" &&
           isFeatureToggleOn("enable_openwrap_pubmatic");
  }

  shouldUsePubmaticAdapterV2() {
    const enabledContentIds = [
      6482, //DAAI TV 
      1561  //RTV
    ]
    
    return enabledContentIds.includes(this.contentId());
  }

  openWrapCallback(callback) {
    return function(appendedTag) {
      this.prerollAdTag().setAdTag(appendedTag);
      callback();
    }.bind(this);
  }
}
